import React from "react";
import { toast } from "react-toastify";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import {
  formatDoorDisplay,
  formatFreightBarcode,
  humanizeEventType,
  humanizeScanMode,
} from "app/data/dock-scan/dockLogMappers";
import { ScanLog } from "app/data/dock-scan/models";
import ScanResultTag from "./ScanResultTag";

interface ScanLogDetailSidebarProps {
  show: boolean;
  scanLog: ScanLog | null;
  onClose: () => void;
}

const copyToClipboard = (text: string) => {
  if (!navigator.clipboard) {
    toast.error("Unable to copy.");
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => toast.info("Copied"),
    () => toast.error("Unable to copy.")
  );
};

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  subValue?: string;
  mono?: boolean;
  copyable?: boolean;
  onCopy?: (text: string) => void;
}> = ({ label, value, subValue, mono, copyable, onCopy }) => {
  const textValue = typeof value === "string" ? value : "";
  return (
    <div className="dock-scan-log-new__detail-row">
      <span className="dock-scan-log-new__detail-label">{label}</span>
      <span className={mono ? "dock-scan-log-new__barcode" : ""}>
        {value}
        {subValue && <span className="text-light xgs-table__cell-subitem"> · {subValue}</span>}
      </span>
      {copyable && onCopy && textValue && textValue !== "—" && (
        <Button
          theme={ButtonThemes.gray}
          size={ButtonSizes.small}
          onClick={() => onCopy(textValue)}
        >
          Copy
        </Button>
      )}
    </div>
  );
};

const ScanLogDetailSidebar: React.FC<ScanLogDetailSidebarProps> = ({
  show,
  scanLog,
  onClose,
}) => {
  return (
    <SlideOutSidebar
      show={show}
      onClose={onClose}
      header="Scan Log Detail"
      size="medium"
    >
      {scanLog && (
        <div className="dock-scan-log-new__detail">
          <DetailRow
            label="Time"
            value={
              <TimestampDisplay
                value={scanLog.scannedAt || scanLog.createdAt}
                format="local"
              />
            }
          />
          <DetailRow
            label="Operator"
            value={
              <DockUserCard
                employeeId={scanLog.employeeId}
                firstName={scanLog.employee?.firstName}
                lastName={scanLog.employee?.lastName}
                keepTooltipInside={false}
              />
            }
          />
          <DetailRow
            label="Equipment"
            value={
              scanLog.equipmentId ? (
                <EquipmentCard unitNumber={scanLog.equipmentId} keepTooltipInside={false} />
              ) : (
                "—"
              )
            }
          />
          <DetailRow label="Barcode" value={formatFreightBarcode(scanLog.barcodeValue)} mono />
          <DetailRow label="Barcode Type" value={scanLog.barcodeType} />
          <DetailRow
            label="Probill"
            value={
              scanLog.probillNumber != null && scanLog.probillNumber !== ""
                ? String(scanLog.probillNumber)
                : "—"
            }
          />
          <DetailRow
            label="Roll"
            value={
              scanLog.rollNumber != null && scanLog.rollNumber !== ""
                ? String(scanLog.rollNumber)
                : "—"
            }
          />
          <DetailRow label="Event" value={humanizeEventType(scanLog.eventType)} />
          <DetailRow label="Scan Mode" value={humanizeScanMode(scanLog.scanMode)} />
          <DetailRow
            label="Result"
            value={<ScanResultTag status={scanLog.resultStatus} />}
            subValue={scanLog.resultCode}
          />
          <DetailRow label="Message" value={scanLog.resultMessage || "—"} />
          <DetailRow label="Door" value={formatDoorDisplay(scanLog.recommendedDoorId) || "—"} />
          <DetailRow label="Trailer" value={scanLog.recommendedTrailerId || "—"} />
          <DetailRow label="Workflow" value={scanLog.workflowType?.replace(/_/g, " ") || "—"} />
          <DetailRow label="Expected Next Scan" value={scanLog.expectedNextScanType || "—"} />
          <DetailRow
            label="Manifest"
            value={scanLog.manifestNumber != null ? String(scanLog.manifestNumber) : "—"}
          />
          <DetailRow label="Manifest Type" value={scanLog.manifestType?.replace(/_/g, " ") || "—"} />
          <DetailRow
            label="Picklist"
            value={scanLog.picklistNumber != null ? String(scanLog.picklistNumber) : "—"}
          />
          <DetailRow
            label="Processing Time"
            value={
              scanLog.processingDurationMs != null ? `${scanLog.processingDurationMs}ms` : "—"
            }
          />
          <DetailRow
            label="Scan Request ID"
            value={scanLog.scanRequestId}
            copyable
            onCopy={copyToClipboard}
          />
          <DetailRow
            label="Correlation ID"
            value={scanLog.correlationId || "—"}
            copyable={!!scanLog.correlationId}
            onCopy={copyToClipboard}
          />
          {scanLog.recommendedLocation && (
            <div className="dock-scan-log-new__detail-section">
              <h4>Recommended Location</h4>
              <pre className="dock-scan-log-new__json">
                {JSON.stringify(scanLog.recommendedLocation, null, 2)}
              </pre>
            </div>
          )}
          {scanLog.metadata && Object.keys(scanLog.metadata).length > 0 && (
            <div className="dock-scan-log-new__detail-section">
              <h4>Metadata</h4>
              <pre className="dock-scan-log-new__json">
                {JSON.stringify(scanLog.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </SlideOutSidebar>
  );
};

export default ScanLogDetailSidebar;
